import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

/**
 * Truck Maintenance — Overview page.
 *
 * Mirrors the Sales Requests overview (RequestsOverviewPage) bucket pattern.
 * Each card is a bucket that routes to its existing landing page. Counts
 * pulled from per-bucket stats endpoints. Cards gracefully render "—" when
 * a user lacks permission to the stats endpoint (no scary error).
 *
 * Buckets (locked May 27, Queue added Jul 2):
 *   1. Work Order
 *   2. Maintenance Queue
 *   3. Incidence
 *   4. Accidents
 *   5. Inspections
 *   6. Maintenance
 *   7. Fleet Status
 *
 * Visibility is enforced server-side by each underlying endpoint — admin sees
 * everything, regular users see what they have permission to see.
 */

// Bucket config — keys, labels, icons, colors, routes, and how to derive the
// count from the stats payload we fetched up top. Keeping the count-getters
// inline keeps the data flow obvious. Colors are per-bucket (not positional)
// so inserting a bucket never reshuffles the theme.
const BUCKETS = [
  {
    key: 'work_order',
    label: 'Work Orders',
    icon: '🔧',
    color: '#3b82f6',
    description: 'Repairs & service work — submissions, history & KPIs.',
    to: '/maintenance/work-orders',
    getCount: (s) => (s.workOrders?.open || 0) + (s.workOrders?.assigned || 0) + (s.workOrders?.in_progress || 0),
    countLabel: 'OPEN',
  },
  {
    key: 'queue',
    label: 'Maintenance Queue',
    icon: '📋',
    color: '#6366f1',
    description: 'Live board — assign, start, complete & close out work.',
    to: '/maintenance/work-orders/queue',
    getCount: (s) => (s.workOrders?.open || 0) + (s.workOrders?.assigned || 0) + (s.workOrders?.in_progress || 0) + (s.workOrders?.completed || 0),
    countLabel: 'IN QUEUE',
  },
  {
    key: 'incidence',
    label: 'Incidents',
    icon: '⚠️',
    color: '#f59e0b',
    description: 'Reported incidents (non-accident).',
    to: '/maintenance/incidents',
    getCount: (s) => Math.max(
      0,
      (s.incidents?.reported || 0)
      + (s.incidents?.under_review || 0)
      + (s.incidents?.work_order_created || 0)
      - (s.incidents?.open_accidents || 0)
    ),
    countLabel: 'OPEN',
  },
  {
    key: 'accidents',
    label: 'Accidents',
    icon: '🚨',
    color: '#dc2626',
    description: 'Accidents requiring review.',
    to: '/maintenance/accidents',
    getCount: (s) => s.incidents?.open_accidents || 0,
    countLabel: 'OPEN',
  },
  {
    key: 'inspections',
    label: 'Inspections',
    icon: '🛡️',
    color: '#10b981',
    description: 'Daily pre/post-trip inspections + compliance.',
    to: '/maintenance/inspections',
    driverTo: '/maintenance/inspect',
    getCount: (s) => s.inspections?.overdueCount || 0,
    countLabel: 'OVERDUE',
  },
  {
    key: 'maintenance',
    label: 'Preventive Maintenance',
    icon: '🛠️',
    color: '#8b5cf6',
    description: 'Interval-based service due / overdue.',
    to: '/maintenance/preventive',
    getCount: (s) => (s.maintenance?.overdue || 0) + (s.maintenance?.due_now || 0),
    countLabel: 'DUE / OVERDUE',
  },
  {
    key: 'fleet_status',
    label: 'Fleet Status',
    icon: '🚚',
    color: '#475569',
    description: 'Trucks down or out of service.',
    to: '/maintenance/fleet-status',
    getCount: (s) => s.fleet?.downCount || 0,
    countLabel: 'TRUCKS DOWN',
  },
];

export default function MaintenanceLandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    workOrders: null,
    incidents: null,
    inspections: null,
    maintenance: null,
    fleet: null,
  });
  const [loading, setLoading] = useState(true);

  const isDriver = (user?.team_name || '') === 'Driver Operations' || user?.role === 'driver';

  useEffect(() => {
    let mounted = true;
    (async () => {
      // Fire every stats request in parallel, swallow per-endpoint failures
      // so one 403 doesn't blank the whole page. Endpoints we can't read just
      // show "—" in the card count.
      const [wo, inc, comp, trucks, maint] = await Promise.all([
        api.getWorkOrderStats?.().catch(() => null),
        api.getIncidentStats?.().catch(() => null),
        api.getInspectionCompliance?.().catch(() => null),
        api.getTrucks?.().catch(() => null),
        api.getMaintenanceStatusSummary?.().catch(() => null),
      ]);
      if (!mounted) return;

      // null = endpoint failed / no permission → card shows "—"
      // empty object/array = endpoint succeeded with no rows → card shows "0"
      const truckList = trucks === null ? null : (Array.isArray(trucks) ? trucks : (trucks?.trucks || []));
      setStats({
        workOrders: wo,
        incidents: inc,
        inspections: comp ? {
          overdueCount: Array.isArray(comp.overdue) ? comp.overdue.length : 0,
        } : null,
        maintenance: maint,
        fleet: truckList === null ? null : {
          downCount: truckList.filter(t => t.status === 'down' || t.status === 'out_of_service').length,
        },
      });
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Truck Maintenance</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
          Click a bucket to open it.
        </p>
      </div>

      <section>
        <h3 style={{
          margin: '0 0 16px',
          fontSize: 20,
          fontWeight: 900,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: '#0f172a',
          borderLeft: '5px solid #D4A843',
          borderBottom: '2px solid #0f172a',
          paddingLeft: 12,
          paddingBottom: 10,
          paddingTop: 4,
        }}>
          Fleet Operations
        </h3>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 16,
        }}>
          {BUCKETS.map((bucket) => {
            const themeColor = bucket.color || '#475569';
            const dest = isDriver && bucket.driverTo ? bucket.driverTo : bucket.to;
            const rawCount = bucket.getCount(stats);
            // Distinguish "no data yet" (loading or no permission) from "0".
            // If every stats source for this bucket came back null, show "—".
            const haveAnyData =
              (bucket.key === 'work_order' && stats.workOrders !== null) ||
              (bucket.key === 'queue' && stats.workOrders !== null) ||
              (bucket.key === 'incidence' && stats.incidents !== null) ||
              (bucket.key === 'accidents' && stats.incidents !== null) ||
              (bucket.key === 'inspections' && stats.inspections !== null) ||
              (bucket.key === 'maintenance' && stats.maintenance !== null) ||
              (bucket.key === 'fleet_status' && stats.fleet !== null);
            const displayCount = loading ? '…' : (haveAnyData ? rawCount : '—');

            return (
              <BucketCard
                key={bucket.key}
                bucket={bucket}
                count={displayCount}
                themeColor={themeColor}
                onClick={() => navigate(dest)}
              />
            );
          })}
        </div>
      </section>
    </div>
  );
}

function BucketCard({ bucket, count, themeColor, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: 20,
        borderRadius: 12,
        border: `1px solid ${themeColor}30`,
        background: `linear-gradient(135deg, ${themeColor}08 0%, transparent 100%)`,
        cursor: 'pointer',
        transition: 'transform 0.1s ease, box-shadow 0.1s ease',
        minHeight: 160,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 4px 16px ${themeColor}25`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 28, lineHeight: 1 }}>{bucket.icon}</span>
        <span style={{ fontWeight: 700, fontSize: 15, color: themeColor }}>
          {bucket.label}
        </span>
      </div>
      <div style={{ fontSize: 48, fontWeight: 800, lineHeight: 1, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
        {count}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {bucket.countLabel}
      </div>
      {bucket.description && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.4 }}>
          {bucket.description}
        </div>
      )}
    </button>
  );
}
